import { clearAuthReturnTo, clearStoredSession, readAuthReturnTo, writeAuthReturnTo } from './auth-storage';
import { authStore } from './auth-store';
import { projectStore } from '@/entities/project/model/project-store';

const HOME_PATH = import.meta.env.VITE_HOME_PATH?.trim() ?? '';

type NavigationUrlParams = Record<string, string | number | null | undefined>;

function normalizeBaseUrl(value: string) {
  return value.endsWith('/') ? value : `${value}/`;
}

function normalizeNavigationParam(value: string | number | null | undefined) {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return '';
}

function resolveNavigationTeamId(teamId?: string | number | null) {
  const preferredTeamId = normalizeNavigationParam(teamId);
  if (preferredTeamId) {
    return preferredTeamId;
  }

  const currentTeamId = normalizeNavigationParam(authStore.loginData?.current_team?.team_id ?? '');
  if (currentTeamId) {
    return currentTeamId;
  }

  const userTeamId = normalizeNavigationParam(authStore.loginData?.user_info?.team_id ?? '');
  if (userTeamId) {
    return userTeamId;
  }

  return normalizeNavigationParam(projectStore.teamId ?? '');
}

function appendNavigationParams(url: URL, params: NavigationUrlParams) {
  for (const [key, value] of Object.entries(params)) {
    const normalizedValue = normalizeNavigationParam(value);
    if (!normalizedValue) {
      continue;
    }

    url.searchParams.set(key, normalizedValue);
  }
}

function resolveReturnToTarget(returnTo?: string | null) {
  const nextTarget = returnTo?.trim();
  return nextTarget || getCurrentHashRouteTarget();
}

export function buildLocationTarget(location: { pathname: string; search: string; hash: string }) {
  return `${location.pathname}${location.search}${location.hash}`;
}

export function buildLoginPath(returnTo?: string | null) {
  if (!returnTo) {
    return '/login';
  }
  const params = new URLSearchParams();
  params.set('redirect', returnTo);
  return `/login?${params.toString()}`;
}

export function getRouterBasePath() {
  if (typeof window !== 'undefined') {
    return window.location.search ? window.location.pathname + window.location.search : window.location.pathname;
  }

  return import.meta.env.BASE_URL;
}

export function getCurrentHashRouteTarget() {
  if (typeof window === 'undefined') {
    return '/';
  }

  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  return hash.startsWith('/') ? hash : '/';
}

export function toHashBrowserUrl(target: string) {
  const safeTarget = target.startsWith('/') ? target : `/${target}`;
  return `${getRouterBasePath()}#${safeTarget}`;
}

export function toAbsoluteBrowserUrl(target: string) {
  if (typeof window === 'undefined') {
    return target;
  }

  return new URL(target, window.location.origin).toString();
}

export function buildAuthReturnToUrl(returnTo?: string | null) {
  return toAbsoluteBrowserUrl(toHashBrowserUrl(resolveReturnToTarget(returnTo)));
}

export function buildAuthLoginUrl(returnTo?: string | null) {
  if (!HOME_PATH) {
    return toAbsoluteBrowserUrl(toHashBrowserUrl(buildLoginPath(returnTo)));
  }

  const loginUrl = new URL('login/', normalizeBaseUrl(HOME_PATH));
  loginUrl.searchParams.set('redirect', buildAuthReturnToUrl(returnTo));
  return loginUrl.toString();
}

export function buildAuthorizedUrl(target: string, params?: NavigationUrlParams) {
  if (!target) {
    return '';
  }

  try {
    const url = typeof window === 'undefined'
      ? new URL(target)
      : new URL(target, window.location.href);

    appendNavigationParams(url, {
      token: authStore.loginData?.user_info?.token ?? '',
      uuid: authStore.loginData?.user_info?.uuid ?? '',
      op_team: resolveNavigationTeamId(params?.op_team ?? null),
    });

    appendNavigationParams(url, params ?? {});
    return url.toString();
  } catch {
    return target;
  }
}

export function buildMemberPurchaseUrl(target: string, options?: { teamId?: string }) {
  const projectTeamId = normalizeNavigationParam(options?.teamId ?? projectStore.teamId);

  return buildAuthorizedUrl(target, {
    op_team: projectTeamId || undefined,
    team_id: projectTeamId || undefined,
    action: 'change_plan',
    duration: '12',
    tab: 'billing',
  });
}

export function buildManagerUrl(options?: { tab?: string; teamId?: string; params?: NavigationUrlParams }) {
  const managerTarget = typeof authStore.loginData?.canvas_urls?.manager === 'string' ? authStore.loginData.canvas_urls.manager.trim() : ''
  if (!managerTarget) {
    return ''
  }

  return buildAuthorizedUrl(managerTarget, {
    op_team: resolveNavigationTeamId(options?.teamId),
    tab: options?.tab?.trim() || undefined,
    ...options?.params,
  })
}

function buildCanvasHomeTargetUrl(target: string) {
  if (!target) {
    return ''
  }

  return buildAuthorizedUrl(target)
}

export function buildCanvasHomeUrl() {
  const homeTarget = typeof authStore.loginData?.canvas_urls?.home === 'string' ? authStore.loginData.canvas_urls.home.trim() : ''
  return buildCanvasHomeTargetUrl(homeTarget)
}

export function buildHomeUrl() {
  const indexTarget = typeof authStore.loginData?.canvas_urls?.index === 'string' ? authStore.loginData.canvas_urls.index.trim() : ''
  const homeTarget = typeof authStore.loginData?.canvas_urls?.home === 'string' ? authStore.loginData.canvas_urls.home.trim() : ''
  const target = indexTarget || homeTarget
  return buildCanvasHomeTargetUrl(target)
}

export function buildRechargeUrl(teamId?: string) {
  const managerTarget = typeof authStore.loginData?.canvas_urls?.manager === 'string' ? authStore.loginData.canvas_urls.manager.trim() : ''
  if (!managerTarget) {
    return ''
  }

  const resolvedTeamId = normalizeNavigationParam(teamId ?? null)

  return buildAuthorizedUrl(managerTarget, {
    op_team: resolvedTeamId || undefined,
    team_id: resolvedTeamId || undefined,
    action: 'buy_point',
  })
}

export function rememberAuthReturnTo(target: string) {
  writeAuthReturnTo(target);
}

export function consumeStoredAuthReturnTo() {
  const target = readAuthReturnTo();
  clearAuthReturnTo();
  return target;
}

export function redirectToLogin(returnTo?: string | null) {
  if (typeof window === 'undefined') {
    return;
  }

  const target = resolveReturnToTarget(returnTo);

  if (HOME_PATH) {
    clearAuthReturnTo();
  } else if (!target.startsWith('/login')) {
    rememberAuthReturnTo(target);
  }

  window.location.assign(buildAuthLoginUrl(target));
}

export function redirectToLoginAfterAuthExpired() {
  if (typeof window === 'undefined') {
    return;
  }

  console.log('[Auth] Auth expired, redirecting to login');
  clearStoredSession();

  const returnTo = getCurrentHashRouteTarget();
  console.log('[Auth] Current path:', returnTo);

  if (!returnTo.startsWith('/login')) {
    const loginUrl = buildAuthLoginUrl(returnTo);
    console.log('[Auth] Redirecting to:', loginUrl);
    redirectToLogin(returnTo);
  } else {
    console.log('[Auth] Already on login page, skipping redirect');
  }
}
